import React, { createContext, useContext, useState, useCallback } from 'react';
import { api } from './AuthContext';
import { Fragrance } from '../components/fragrance/FragranceCard';

interface FragranceFilters {
  search?: string;
  house?: string;
  notes?: string;
  concentration?: string;
  is_favorite?: boolean;
  is_decant?: boolean;
  min_rating?: number;
  max_rating?: number;
  winter_rating?: boolean;
  spring_rating?: boolean;
  summer_rating?: boolean;
  fall_rating?: boolean;
  day_rating?: boolean;
  night_rating?: boolean;
}

interface FragranceSorting {
  sort_by: string;
  sort_order: 'asc' | 'desc';
}

interface FragrancePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface FragranceCollection {
  fragrances: Fragrance[];
  pagination: FragrancePagination;
}

interface CollectionStats {
  totals: {
    fragrances: number;
    decants: number;
    favorites: number;
    houses: number;
  };
  topHouses: Array<{ house: string; count: number }>;
  averageRatings: {
    overall: number;
    longevity: number;
    sillage: number;
    seasonal: {
      winter: number;
      spring: number;
      summer: number;
      fall: number;
    };
  };
  concentrations: Array<{ concentration: string; count: number }>;
  topNotes: Array<{ note: string; count: number }>;
}

interface FragranceContextType {
  // Data
  collection: FragranceCollection | null;
  stats: CollectionStats | null;
  isLoading: boolean;
  error: string | null;
  
  // Filters and sorting
  filters: FragranceFilters;
  sorting: FragranceSorting;
  pageSize: number;
  
  // Actions
  fetchFragrances: (filters?: FragranceFilters, sorting?: FragranceSorting, page?: number, pageSize?: number) => Promise<void>;
  createFragrance: (fragranceData: Partial<Fragrance>) => Promise<{ success: boolean; message: string; fragrance?: Fragrance }>;
  updateFragrance: (id: number, fragranceData: Partial<Fragrance>) => Promise<{ success: boolean; message: string; fragrance?: Fragrance }>;
  deleteFragrance: (id: number) => Promise<{ success: boolean; message: string }>;
  toggleFavorite: (id: number) => Promise<{ success: boolean; message: string; fragrance?: Fragrance }>;
  fetchStats: () => Promise<void>;
  getAutocompleteSuggestions: (type: 'house' | 'note' | 'fragrance_name', query?: string) => Promise<string[]>;
  
  // Filter and sort management
  setFilters: (filters: FragranceFilters) => void;
  setSorting: (sorting: FragranceSorting) => void;
  setPageSize: (size: number) => void;
  clearFilters: () => void;
  
  // Utility
  copyFragranceDetails: (fragrance: Fragrance) => void;
  refreshCollection: () => Promise<void>;
}

const FragranceContext = createContext<FragranceContextType | undefined>(undefined);

export const useFragrances = () => {
  const context = useContext(FragranceContext);
  if (context === undefined) {
    throw new Error('useFragrances must be used within a FragranceProvider');
  }
  return context;
};

interface FragranceProviderProps {
  children: React.ReactNode;
}

export const FragranceProvider: React.FC<FragranceProviderProps> = ({ children }) => {
  const [collection, setCollection] = useState<FragranceCollection | null>(null);
  const [stats, setStats] = useState<CollectionStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [filters, setFiltersState] = useState<FragranceFilters>({});
  const [sorting, setSortingState] = useState<FragranceSorting>({
    sort_by: 'house',
    sort_order: 'asc'
  });
  const [pageSize, setPageSizeState] = useState(() => {
    const saved = localStorage.getItem('fragrance-organizer-page-size');
    return saved ? parseInt(saved) : 50;
  });

  const setFilters = useCallback((newFilters: FragranceFilters) => {
    setFiltersState(newFilters);
  }, []);

  const setSorting = useCallback((newSorting: FragranceSorting) => {
    setSortingState(newSorting);
  }, []);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    localStorage.setItem('fragrance-organizer-page-size', size.toString());
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState({});
  }, []);

  const fetchFragrances = useCallback(async (
    customFilters?: FragranceFilters,
    customSorting?: FragranceSorting,
    page: number = 1,
    customPageSize?: number
  ) => {
    try {
      setIsLoading(true);
      setError(null);

      const queryFilters = customFilters || filters;
      const querySorting = customSorting || sorting;
      const limit = customPageSize ?? pageSize;

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: querySorting.sort_by,
        sort_order: querySorting.sort_order,
      });

      // Add filters to query params
      Object.entries(queryFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/fragrances?${params.toString()}`);
      
      if (response.data.success) {
        setCollection(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch fragrances');
      }
    } catch (err: any) {
      console.error('Fetch fragrances error:', err);
      setError(err.response?.data?.message || 'Failed to fetch fragrances');
    } finally {
      setIsLoading(false);
    }
  }, [filters, sorting, pageSize]); // Remove dependencies to prevent infinite loops

  const createFragrance = useCallback(async (fragranceData: Partial<Fragrance>) => {
    try {
      setError(null);
      const response = await api.post('/fragrances', fragranceData);
      
      if (response.data.success) {
        // Refresh the collection to include the new fragrance
        await fetchFragrances();
        return { 
          success: true, 
          message: response.data.message,
          fragrance: response.data.data.fragrance 
        };
      } else {
        return { success: false, message: response.data.message || 'Failed to create fragrance' };
      }
    } catch (err: any) {
      console.error('Create fragrance error:', err);
      const message = err.response?.data?.message || 'Failed to create fragrance';
      setError(message);
      return { success: false, message };
    }
  }, [fetchFragrances]);

  const updateFragrance = useCallback(async (id: number, fragranceData: Partial<Fragrance>) => {
    try {
      setError(null);
      const response = await api.put(`/fragrances/${id}`, fragranceData);
      
      if (response.data.success) {
        // Update the fragrance in the local collection
        if (collection) {
          const updatedFragrances = collection.fragrances.map(f => 
            f.id === id ? response.data.data.fragrance : f
          );
          setCollection({
            ...collection,
            fragrances: updatedFragrances
          });
        }
        
        return { 
          success: true, 
          message: response.data.message,
          fragrance: response.data.data.fragrance 
        };
      } else {
        return { success: false, message: response.data.message || 'Failed to update fragrance' };
      }
    } catch (err: any) {
      console.error('Update fragrance error:', err);
      const message = err.response?.data?.message || 'Failed to update fragrance';
      setError(message);
      return { success: false, message };
    }
  }, [collection]);

  const deleteFragrance = useCallback(async (id: number) => {
    try {
      setError(null);
      const response = await api.delete(`/fragrances/${id}`);
      
      if (response.data.success) {
        // Remove the fragrance from the local collection
        if (collection) {
          const updatedFragrances = collection.fragrances.filter(f => f.id !== id);
          setCollection({
            ...collection,
            fragrances: updatedFragrances
          });
        }
        
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message || 'Failed to delete fragrance' };
      }
    } catch (err: any) {
      console.error('Delete fragrance error:', err);
      const message = err.response?.data?.message || 'Failed to delete fragrance';
      setError(message);
      return { success: false, message };
    }
  }, [collection]);

  const toggleFavorite = useCallback(async (id: number) => {
    try {
      setError(null);
      const response = await api.patch(`/fragrances/${id}/favorite`);
      
      if (response.data.success) {
        // Update the fragrance in the local collection
        if (collection) {
          const updatedFragrances = collection.fragrances.map(f => 
            f.id === id ? response.data.data.fragrance : f
          );
          setCollection({
            ...collection,
            fragrances: updatedFragrances
          });
        }
        
        return { 
          success: true, 
          message: response.data.message,
          fragrance: response.data.data.fragrance 
        };
      } else {
        return { success: false, message: response.data.message || 'Failed to toggle favorite' };
      }
    } catch (err: any) {
      console.error('Toggle favorite error:', err);
      const message = err.response?.data?.message || 'Failed to toggle favorite';
      setError(message);
      return { success: false, message };
    }
  }, [collection]);

  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      const response = await api.get('/fragrances/stats');
      
      if (response.data.success) {
        setStats(response.data.data.stats);
      } else {
        setError(response.data.message || 'Failed to fetch statistics');
      }
    } catch (err: any) {
      console.error('Fetch stats error:', err);
      setError(err.response?.data?.message || 'Failed to fetch statistics');
    }
  }, []);

  const getAutocompleteSuggestions = useCallback(async (
    type: 'house' | 'note' | 'fragrance_name',
    query: string = ''
  ): Promise<string[]> => {
    try {
      const response = await api.get(`/fragrances/suggestions?type=${type}&query=${encodeURIComponent(query)}`);
      
      if (response.data.success) {
        return response.data.data.suggestions;
      } else {
        console.error('Failed to fetch suggestions:', response.data.message);
        return [];
      }
    } catch (err: any) {
      console.error('Autocomplete suggestions error:', err);
      return [];
    }
  }, []);

  const copyFragranceDetails = useCallback((fragrance: Fragrance) => {
    const details = [
      `${fragrance.name} by ${fragrance.house}`,
      fragrance.concentration ? `Concentration: ${fragrance.concentration}` : '',
      fragrance.size_oz || fragrance.size_ml ? `Size: ${fragrance.size_oz ? fragrance.size_oz + ' oz' : ''}${fragrance.size_oz && fragrance.size_ml ? ' / ' : ''}${fragrance.size_ml ? fragrance.size_ml + ' ml' : ''}` : '',
      fragrance.notes_top ? `Top Notes: ${fragrance.notes_top}` : '',
      fragrance.notes_middle ? `Middle Notes: ${fragrance.notes_middle}` : '',
      fragrance.notes_base ? `Base Notes: ${fragrance.notes_base}` : '',
      fragrance.overall_rating ? `Overall Rating: ${fragrance.overall_rating}/10` : '',
      fragrance.longevity ? `Longevity: ${fragrance.longevity}/5` : '',
      fragrance.sillage ? `Sillage: ${fragrance.sillage}/4` : '',
      fragrance.winter_rating ? `Winter Rating: ${fragrance.winter_rating}/5` : '',
      fragrance.spring_rating ? `Spring Rating: ${fragrance.spring_rating}/5` : '',
      fragrance.summer_rating ? `Summer Rating: ${fragrance.summer_rating}/5` : '',
      fragrance.fall_rating ? `Fall Rating: ${fragrance.fall_rating}/5` : '',
      fragrance.day_rating ? `Day Rating: ${fragrance.day_rating}/5` : '',
      fragrance.night_rating ? `Night Rating: ${fragrance.night_rating}/5` : '',
      fragrance.personal_notes ? `Personal Notes: ${fragrance.personal_notes}` : '',
      fragrance.is_favorite ? `Favorite: Yes` : '',
      fragrance.is_decant ? `Type: Decant/Sample` : '',
      fragrance.is_clone && fragrance.original_fragrance ? `Clone of: ${fragrance.original_fragrance}` : '',
    ].filter(Boolean).join('\n');

    // Check if navigator.clipboard is available (HTTPS or localhost)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(details).then(() => {
        console.log('Fragrance details copied to clipboard');
      }).catch(err => {
        console.error('Failed to copy to clipboard:', err);
      });
    } else {
      // Fallback for older browsers or HTTP
      try {
        const textArea = document.createElement('textarea');
        textArea.value = details;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
        console.log('Fragrance details copied to clipboard (fallback)');
      } catch (err) {
        console.error('Failed to copy to clipboard (fallback):', err);
      }
    }
  }, []);

  const refreshCollection = useCallback(async () => {
    await fetchFragrances();
  }, [fetchFragrances]);

  const value = {
    collection,
    stats,
    isLoading,
    error,
    filters,
    sorting,
    pageSize,
    fetchFragrances,
    createFragrance,
    updateFragrance,
    deleteFragrance,
    toggleFavorite,
    fetchStats,
    getAutocompleteSuggestions,
    setFilters,
    setSorting,
    setPageSize,
    clearFilters,
    copyFragranceDetails,
    refreshCollection,
  };

  return (
    <FragranceContext.Provider value={value}>
      {children}
    </FragranceContext.Provider>
  );
};

export default FragranceProvider;
